import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Images from "../utils/Images";

const EASE = [0.22, 1, 0.36, 1];

const AUDIENCES = ["Banks", "Insurers", "FinTechs", "Institutions"];

const BENEFITS = [
  "Qualified Customer Leads",
  "Lower Marketing Costs",
  "Higher Conversion Rates",
  "Digital Verification",
  "Intelligent Collections",
  "Business Intelligence",
  "API Integration",
  "Dedicated Support",
];

function Partner() {
  const reduce = useReducedMotion();
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  return (
    <section
      id="partner"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="partner-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_45%_at_0%_0%,rgba(229,184,11,0.1),transparent_55%),radial-gradient(50%_40%_at_100%_20%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.3]"
          style={{
            backgroundImage: "radial-gradient(rgba(12,31,27,0.08) 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative mx-auto grid max-w-8xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16 lg:px-8 lg:py-28">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
            Become a partner
          </p>
          <h2
            id="partner-heading"
            className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,5vw,3.75rem)]"
          >
            Grow your business
            <span className="mt-1 block">with SimFinance</span>
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
            Join banks, insurers, fintechs and financial institutions already transforming customer
            acquisition and digital engagement.
          </p>

          <ul className="mt-6 flex flex-wrap gap-2" aria-label="Partner types">
            {AUDIENCES.map((item) => (
              <li
                key={item}
                className="border border-navy/10 bg-[#f3f7f5] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70"
              >
                {item}
              </li>
            ))}
          </ul>

          <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
            Benefits
          </p>
          <ul className="mt-4 grid gap-x-8 sm:grid-cols-2">
            {BENEFITS.map((item, i) => (
              <motion.li
                key={item}
                className="flex items-start gap-3 border-t border-navy/10 py-3.5"
                initial={reduce ? false : { opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={
                  reduce ? { duration: 0 } : { duration: 0.35, delay: 0.04 * i, ease: EASE }
                }
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy text-white">
                  <CheckIcon />
                </span>
                <span className="text-[15px] leading-snug text-navy/80">{item}</span>
              </motion.li>
            ))}
          </ul>

          <a
            href="#contact"
            className="mt-10 inline-flex min-h-12 cursor-pointer items-center gap-2 rounded-full bg-gold px-6 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
          >
            Become a Partner
            <ArrowIcon />
          </a>
        </motion.div>

        <motion.div
          className="relative"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fade}
        >
          <span className="pointer-events-none absolute -left-2 -top-2 h-10 w-10 border-l border-t border-gold" />
          <span className="pointer-events-none absolute -right-2 -top-2 h-10 w-10 border-r border-t border-navy/20" />
          <span className="pointer-events-none absolute -bottom-2 -left-2 h-10 w-10 border-b border-l border-navy/20" />
          <span className="pointer-events-none absolute -bottom-2 -right-2 h-10 w-10 border-b border-r border-gold" />

          <div className="relative min-h-[22rem] overflow-hidden bg-navy lg:min-h-[34rem]">
            <MediaFrame
              src={Images.partnerVisual}
              alt="Financial institution leaders partnering with SimFinance to grow customer acquisition"
              label="01  ·  Partner"
              position="object-center"
              className="absolute inset-0 h-full w-full"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/20" />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
              <p className="font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
                Partnership
              </p>
              <p className="mt-2 max-w-sm text-lg font-semibold tracking-tight text-white">
                One ecosystem. Qualified growth.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function MediaFrame({ src, alt, label, className, position = "object-center" }) {
  const [failed, setFailed] = useState(false);
  const showFallback = failed || !src;

  if (showFallback) {
    return (
      <div className={`relative overflow-hidden bg-navy ${className}`} role="img" aria-label={alt}>
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -right-10 top-8 h-40 w-40 rounded-full bg-royal/35 blur-3xl" />
        <div className="absolute -bottom-12 left-6 h-32 w-32 rounded-full bg-gold/20 blur-3xl" />
        <div className="relative flex h-full min-h-[inherit] flex-col justify-between p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gold/90">{label}</p>
          <p className="max-w-[14rem] text-sm leading-relaxed text-white/55">
            Placeholder — generate and drop the image into this slot.
          </p>
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`object-cover ${position} ${className}`}
      onError={() => setFailed(true)}
    />
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3 w-3">
      <path
        d="M2.4 6.2L4.8 8.6L9.6 3.4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Partner;
